import React, { useCallback } from 'react';

import { Stack } from '@atlaskit/primitives/compiled';

import { type ContentOptionValue, useConfigPanelOptions } from './config-panel-options';
import type {
	ManagedBlockConfiguration,
	ManagedBlockConfigurationUpdate,
	ManagedBlockGoal,
	ManagedBlockOutputFormat,
} from './configuration';
import { GoalSelect } from './GoalSelect';
import { OutputFormatPicker } from './OutputFormatPicker';
import type { SmartCardProviderProduct } from './sources/smart-card-product';
import { SourcesPanel } from './sources/SourcesPanel';

type ConfigPanelProps = {
	additionalContextValidationError: boolean;
	configuration: ManagedBlockConfiguration;
	isDisabled?: boolean;
	onChange: (update: ManagedBlockConfigurationUpdate) => void;
	onConfigEdit: () => void;
	onInteraction: () => void;
	onPromptSelect: () => void;
	showInitialSourceDisclaimer?: boolean;
	smartCardProviderProduct?: SmartCardProviderProduct;
	sourceValidationError: 'invalid' | 'pending' | 'required' | null;
};

export const ConfigPanel = ({
	additionalContextValidationError,
	configuration,
	isDisabled = false,
	onChange,
	onConfigEdit,
	onInteraction,
	onPromptSelect,
	smartCardProviderProduct,
	showInitialSourceDisclaimer,
	sourceValidationError,
}: ConfigPanelProps): JSX.Element => {
	const { goalOptions, outputFormatOptions, sourceModeOptions, systemDesignOutputFormatOptions } =
		useConfigPanelOptions();

	const handleGoalChange = useCallback(
		(nextGoal: ManagedBlockGoal) => {
			const nextOutputFormat =
				nextGoal === 'system-design'
					? 'diagram'
					: configuration.outputFormat === 'diagram'
						? 'text'
						: configuration.outputFormat;

			onInteraction();

			if (configuration.goal !== nextGoal || configuration.outputFormat !== nextOutputFormat) {
				onConfigEdit();
			}

			onChange((currentConfiguration) => {
				const currentNextOutputFormat =
					nextGoal === 'system-design'
						? 'diagram'
						: currentConfiguration.outputFormat === 'diagram'
							? 'text'
							: currentConfiguration.outputFormat;

				return {
					...currentConfiguration,
					goal: nextGoal,
					outputFormat: currentNextOutputFormat,
				};
			});
		},
		[configuration.goal, configuration.outputFormat, onChange, onConfigEdit, onInteraction],
	);

	const handleContentSelectionChange = useCallback(
		(nextSelection: ContentOptionValue) => {
			if (nextSelection === 'prompt') {
				onPromptSelect();
				return;
			}

			handleGoalChange(nextSelection);
		},
		[handleGoalChange, onPromptSelect],
	);

	const handleOutputFormatChange = useCallback(
		(outputFormat: ManagedBlockOutputFormat) => {
			onInteraction();

			if (configuration.outputFormat !== outputFormat) {
				onConfigEdit();
			}

			onChange((currentConfiguration) => ({
				...currentConfiguration,
				outputFormat,
			}));
		},
		[configuration.outputFormat, onChange, onConfigEdit, onInteraction],
	);

	return (
		<Stack space="space.150">
			<GoalSelect
				contentSelection={configuration.goal}
				isDisabled={isDisabled}
				onContentSelectionChange={handleContentSelectionChange}
				options={goalOptions}
			/>
			<OutputFormatPicker
				goal={configuration.goal}
				isDisabled={isDisabled}
				onOutputFormatChange={handleOutputFormatChange}
				outputFormat={configuration.outputFormat}
				outputFormatOptions={outputFormatOptions}
				systemDesignOutputFormatOptions={systemDesignOutputFormatOptions}
			/>
			<SourcesPanel
				additionalContext={configuration.additionalContext}
				additionalContextValidationError={additionalContextValidationError}
				isDisabled={isDisabled}
				isAdditionalContextSelected={configuration.isAdditionalContextSelected}
				onChange={onChange}
				onConfigEdit={onConfigEdit}
				onInteraction={onInteraction}
				sourceEntries={configuration.sourceEntries}
				sourceMode={configuration.sourceMode}
				sourceModeOptions={sourceModeOptions}
				showInitialSourceDisclaimer={showInitialSourceDisclaimer}
				smartCardProviderProduct={smartCardProviderProduct}
				sourceValidationError={sourceValidationError}
				sourceUrls={configuration.sourceUrls}
			/>
		</Stack>
	);
};
